import type { FC, ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import Path from "src/constants/path";
import { createAppPath, preserveQueryParams } from "src/lib/createAppPath";

export const LEGACY_RX_DIET_POPUP = "rxDietNutritionProgram";

type LegacyRedirectInput = {
  pathname: string;
  search: string;
  params?: {
    module?: string;
    task?: string;
  };
};

const legacyPathCandidates = (target: string) => [
  target,
  `/${target}`,
  `${Path.root}${Path.app}/${target}`
];

const matchesLegacyPath = (pathname: string, target: string) =>
  legacyPathCandidates(target).includes(pathname);

const LEGACY_PROGRAM_PREFIXES = legacyPathCandidates("program").map(
  (target) => `${target}/`
);
const LEGACY_JOURNEY_PATHS = new Set(
  ["journey", "path-to-healthy-weight", "program/cdcdpp"].flatMap((suffix) =>
    legacyPathCandidates(Path.myPlan).map((target) => `${target}/${suffix}`)
  )
);

const buildJourneyPath = (params?: LegacyRedirectInput["params"]) => {
  const pathSegments: string[] = [createAppPath(Path.journey)];

  if (params?.module) {
    pathSegments.push(params.module);

    if (params.task) {
      pathSegments.push(params.task);
    }
  }

  return pathSegments.join("/");
};

const getLegacyProgramParams = (pathname: string) => {
  const prefix = LEGACY_PROGRAM_PREFIXES.find((item) =>
    pathname.startsWith(item)
  );

  if (!prefix) {
    return undefined;
  }

  const pathSegments = pathname.slice(prefix.length).split("/").filter(Boolean);

  return {
    module: pathSegments[1],
    task: pathSegments[2]
  };
};

export const resolveLegacyRedirectPath = ({
  pathname,
  search,
  params
}: LegacyRedirectInput): string | null => {
  const searchParams = new URLSearchParams(search);
  const isLegacyMealsLink =
    searchParams.get("popup") === LEGACY_RX_DIET_POPUP &&
    [Path.home, Path.myPlan].some((target) =>
      matchesLegacyPath(pathname, target)
    );

  if (isLegacyMealsLink) {
    searchParams.delete("popup");
    const remainingSearch = searchParams.toString();
    const targetPath = createAppPath(Path.meals);

    return remainingSearch ? `${targetPath}?${remainingSearch}` : targetPath;
  }

  if (
    LEGACY_PROGRAM_PREFIXES.some((prefix) => pathname.startsWith(prefix)) ||
    LEGACY_JOURNEY_PATHS.has(pathname)
  ) {
    return preserveQueryParams(
      buildJourneyPath(params ?? getLegacyProgramParams(pathname)),
      search
    );
  }

  return null;
};

type LegacyRedirectsProps = {
  fallback?: ReactNode;
};

const LegacyRedirects: FC<LegacyRedirectsProps> = ({ fallback = null }) => {
  const location = useLocation();
  const { module, task } = useParams();
  const redirectPath = resolveLegacyRedirectPath({
    pathname: location.pathname,
    search: location.search,
    params: { module, task }
  });

  if (!redirectPath) {
    return fallback;
  }

  return <Navigate to={redirectPath} replace />;
};

export default LegacyRedirects;
